/* Sentry first — see `instrument.ts` for why the order matters. */
import "./instrument";
import * as Sentry from "@sentry/react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import { I18nProvider } from "./shared/i18n";
import { startOutbox } from "./shared/offline";
import { startServiceWorker } from "./shared/pwa/serviceWorker";
import { captureDeviceOverride } from "./shared/device/deviceProfile";
import "./shared/styles/index.css";
import { rewritePublicPath } from "./app/router/pathToHash";

/* `/contact-sales` → `/#/contact-sales` before the router reads the hash. */
rewritePublicPath();

const container = document.getElementById("root");
if (!container) throw new Error("Root element #root not found");

/*
 * The outbox's triggers, wired before the first render — `online`, `focus`, and
 * the post-registration drain (offline-sync.md §4, §8). It is inert until
 * something is enqueued: no timer runs on an empty queue, and nothing is sent
 * before the device has a token.
 *
 * Deliberately NOT inside a component: the queue must keep flushing from a timer
 * and a window event, neither of which belongs to React's lifecycle.
 */
startOutbox();

/*
 * The offline cache, and keeping a long-open tab on the current build — see
 * `shared/pwa/serviceWorker.ts` for why this is no longer an injected script.
 */
startServiceWorker();

/* A QA `?device=low|mid|high|auto` override — see `shared/device/deviceProfile`. */
captureDeviceOverride();

/*
 * React 19's root error hooks, so Sentry sees render errors with their component
 * stack — including the ones an error boundary catches, which otherwise never
 * reach `window.onerror`. An uncaught one is still logged locally as well.
 */
createRoot(container, {
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.error("Uncaught render error", error, errorInfo.componentStack);
  }),
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
}).render(
  <StrictMode>
    {/*
      The provider is what puts `lang` on the document root. The product ships
      English only, so the value never changes — but the attribute is what
      picks a screen reader's voice, so it is set rather than assumed.
    */}
    <I18nProvider>
      <App />
    </I18nProvider>
  </StrictMode>,
);
